import {
  GamewearFilterForm,
  GamewearFilterKey,
  GamewearSearchParams,
  RecolorOption,
  StoreType,
} from 'types/marketplace/confirmItem';
import { GameType } from 'types/marketplace';
import { arrayValueToQueryString, valueToQueryString } from 'modules/utils/queryString';
import { createFilterFormGetter, createSearchParamsParser } from 'modules/utils/filterUtils';
import {
  GAMEWEAR_ARRAY_VALUE_KEYS,
  gamewearInitialSearchParams,
} from 'constants/marketplace/gamewearItem';
import { GAMEWEAR_FILTERS } from 'constants/marketplace/gamewearFilterConfig';

export const getGamewearFilterForm = createFilterFormGetter<GamewearFilterForm>(GAMEWEAR_FILTERS);

export const getGamewearSearchParams = createSearchParamsParser<GamewearSearchParams>(
  GAMEWEAR_FILTERS,
  gamewearInitialSearchParams,
);

// Context 필터: 빈 배열이면 파라미터 생략 (전체 조회)
const CONTEXT_FILTER_KEY_SET = new Set<string>(Object.values(GamewearFilterKey));

// gameType별 context filter 매핑
const INZOI_FILTER_KEYS = new Set<string>([GamewearFilterKey.INZOI_RECOLOR]);

const VRCHAT_FILTER_KEYS = new Set<string>([
  GamewearFilterKey.VRCHAT_AVATAR_COMPATIBILITY,
  GamewearFilterKey.VRCHAT_PLATFORM,
  GamewearFilterKey.VRCHAT_ITEM_TYPE,
]);

const GAMEWEAR_ARRAY_VALUE_KEY_SET = new Set(GAMEWEAR_ARRAY_VALUE_KEYS);

export const getGamewearSearchApiQueryString = (searchParams: GamewearSearchParams): string => {
  const selectedGameTypes = searchParams.gameTypes ?? [];

  const queryParts = Object.entries(searchParams)
    .map(([key, value]) => {
      const filterDef = GAMEWEAR_FILTERS[key];
      const apiParamName = filterDef?.apiKey ?? key;

      // 정확히 1개 gameType 선택 시에만 해당 context filter 포함, 그 외 모두 제외
      if (CONTEXT_FILTER_KEY_SET.has(key)) {
        if (selectedGameTypes.length !== 1) return '';
        if (INZOI_FILTER_KEYS.has(key) && !selectedGameTypes.includes(GameType.INZOI)) return '';
        if (VRCHAT_FILTER_KEYS.has(key) && !selectedGameTypes.includes(GameType.VRCHAT)) return '';
      }

      // Context 필터: 빈 배열이면 param 생략 (전체 조회)
      if (CONTEXT_FILTER_KEY_SET.has(key) && Array.isArray(value) && value.length === 0) {
        return '';
      }

      // inzoiRecolor: number[] → boolean 변환
      if (key === GamewearFilterKey.INZOI_RECOLOR && Array.isArray(value)) {
        if (value.length === 2) return ''; // 전체 선택 = 생략
        if (value.includes(RecolorOption.AVAILABLE)) return `${apiParamName}=true`;
        if (value.includes(RecolorOption.UNAVAILABLE)) return `${apiParamName}=false`;
        return '';
      }

      if (GAMEWEAR_ARRAY_VALUE_KEY_SET.has(key)) {
        return arrayValueToQueryString(apiParamName, value as Array<string | number>);
      }
      return valueToQueryString(apiParamName, value);
    })
    .filter(querystring => !!querystring);

  // 항상 storeType=2 (GAMEWEAR) 포함
  queryParts.push(`storeType=${StoreType.GAMEWEAR}`);

  return queryParts.join('&');
};

export const convertToGamewearSearchApiQueryString = (pageQueryString: string): string => {
  const searchParams = getGamewearSearchParams(pageQueryString);
  return getGamewearSearchApiQueryString(searchParams);
};
